import {SelectionRange, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeShouldShowDragHandles,
  computeTableDraggingState,
} from 'notebook/tables/store/logic/TableReorderingLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

import {TableDimensionsModel} from './TableDimensionsSelectors';

/**
 * Model containing derived state for row/column reordering (drag handles and the active drag).
 */
export interface TableReorderingModel {
  /**
   * Whether the row reordering drag handle should be shown.
   */
  shouldShowRowDragHandles: boolean;

  /**
   * Whether the column reordering drag handle should be shown.
   */
  shouldShowColumnDragHandles: boolean;

  /**
   * The dimension currently being dragged, or null when nothing is being dragged.
   */
  draggingDimension: TableDimension | null;

  /**
   * The range currently being dragged, or null when nothing is being dragged.
   */
  draggingRange: SelectionRange | null;
}

/**
 * Creates a memoized selector that computes the TableReorderingModel from the store.
 */
export function createTableReorderingSelector(
  tableDimensionsSelector: (store: TableEditableStore) => TableDimensionsModel
): (store: TableEditableStore) => TableReorderingModel {
  return createTableModelSelector(
    [
      (store) => store.stableProps.locked,
      (store) => store.stableProps.fileId,
      (store) => store.stableProps.isProtected ?? false,
      (store) => store.stableProps.allowReorderDimensions,
      (store) => store.volatileProps.table,
      (store) => tableDimensionsSelector(store).canModifyRowDim,
      (store) => tableDimensionsSelector(store).canModifyColumnDim,
      (store) => store.state.draggingDimension,
      (store) => store.state.multiStartIndex,
      (store) => store.state.multiEndIndex,
    ],
    (
      locked,
      fileId,
      isProtected,
      allowReorderDimensions,
      table,
      canModifyRowDim,
      canModifyColumnDim,
      draggingDimension,
      multiStartIndex,
      multiEndIndex
    ): TableReorderingModel => {
      const dragHandlesParams = {
        locked,
        fileId,
        isProtected,
        allowReorderDimensions,
        table,
        canModifyRowDim,
        canModifyColumnDim,
      };
      const {dimension, range} = computeTableDraggingState(
        locked,
        draggingDimension,
        multiStartIndex,
        multiEndIndex
      );
      return {
        shouldShowRowDragHandles: computeShouldShowDragHandles(TableDimension.ROW, dragHandlesParams),
        shouldShowColumnDragHandles: computeShouldShowDragHandles(TableDimension.COLUMN, dragHandlesParams),
        draggingDimension: dimension,
        draggingRange: range,
      };
    }
  );
}
